import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useDeveloperMode } from '@/hooks/useDeveloperMode';
import { useLanguage } from '@/context/LanguageContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import { 
  Building2, 
  Palette, 
  Users, 
  Download,
  Shield,
  Save,
  Plus,
  UserX,
  Check,
  FileText,
  Globe,
  CreditCard,
  DollarSign,
  Banknote,
  Wallet,
  Link as LinkIcon,
  Building,
  HelpCircle,
  Mail,
  Send,
  CheckCircle,
  AlertCircle,
  UserCog,
  Upload,
  Trash2,
  Edit,
  MoreVertical,
  Bell,
  Star,
  Lock,
  Settings,
  Archive,
  RotateCcw,
  User,
  MessageSquare,
  Smartphone,
  Phone,
  FileSignature,
  Clock,
  Hash,
  RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import StripeConnectCard from '@/components/StripeConnectCard';
import { Badge } from '@/components/ui/badge';

const SERVICE_TYPES = [
  'Concrete', 'Landscaping', 'Roofing', 'HVAC', 'Cleaning',
  'Plumbing', 'Electrical', 'Painting', 'Fencing', 'Flooring',
  'Decking', 'General Construction', 'Lawn Care', 'Tree Service', 
  'Snow Removal', 'Pressure Washing', 'Pool Service', 'Other'
];

const COLOR_PRESETS = [
  { name: 'Classic Blue', primary: '#1e3a8a', accent: '#fbbf24' },
  { name: 'Forest Green', primary: '#166534', accent: '#f59e0b' },
  { name: 'Slate Gray', primary: '#334155', accent: '#06b6d4' },
  { name: 'Brick Red', primary: '#991b1b', accent: '#fbbf24' },
  { name: 'Royal Purple', primary: '#581c87', accent: '#f97316' },
];

const DEFAULT_ESTIMATE_TERMS = `• Estimate valid for 30 days from date issued
• 50% deposit required to schedule work
• Balance due upon completion
• Change orders may adjust final price
• Work schedule dependent on weather and material availability
• We accept: Credit Card, Check, Cash, ACH Transfer`;

// Helper to format role names for display
const formatRoleName = (role) => {
  if (!role) return 'Staff';
  const roleMap = {
    'owner': 'Owner',
    'admin': 'Admin',
    'manager': 'Manager',
    'staff': 'Staff',
    'crew_member': 'Crew Member',
    'crew_lead': 'Crew Lead',
    'field_estimator': 'Field Estimator',
    'project_manager': 'Project Manager',
    'subcontractor': 'Subcontractor',
    'estimator': 'Estimator'
  };
  return roleMap[role] || role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

// Follow-Up Settings Section (used in Documents tab)
const FollowUpSettingsSection = () => {
  const { organization, user } = useAuth();
  const { t } = useLanguage();
  const [settings, setSettings] = useState({
    enabled: false, reminder_days: [3, 7, 14], max_reminders: 3, message_template: '', include_pdf: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [newDay, setNewDay] = useState('');

  useEffect(() => {
    if (organization?.id) loadSettings();
  }, [organization?.id]);

  const loadSettings = async () => {
    try {
      const res = await apiHelpers.getFollowUpSettings(organization.id);
      setSettings(res.data);
    } catch { /* defaults */ }
    finally { setIsLoading(false); }
  };

  const handleSave = async () => {
    try {
      await apiHelpers.updateFollowUpSettings(organization.id, settings, user.id);
      toast.success('Follow-up settings saved');
    } catch { toast.error('Failed to save'); }
  };

  const addDay = () => {
    const d = parseInt(newDay);
    if (d > 0 && !settings.reminder_days.includes(d)) {
      setSettings(prev => ({ ...prev, reminder_days: [...prev.reminder_days, d].sort((a, b) => a - b) }));
      setNewDay('');
    }
  };

  const removeDay = (day) => {
    setSettings(prev => ({ ...prev, reminder_days: prev.reminder_days.filter(d => d !== day) }));
  };

  if (isLoading) return null;

  // Check if feature is enabled at org level
  const featureEnabled = organization?.feature_flags?.follow_ups;
  if (!featureEnabled) {
    return (
      <div className="card-default opacity-60" data-testid="follow-up-settings-disabled">
        <h2 className="font-heading text-xl font-semibold mb-2">Automated Follow-Ups</h2>
        <p className="text-muted-foreground text-sm">This feature is not enabled for your organization. Contact your admin to enable it.</p>
      </div>
    );
  }

  return (
    <div className="card-default" data-testid="follow-up-settings-section">
      <h2 className="font-heading text-xl font-semibold mb-2">Automated Follow-Ups</h2>
      <p className="text-muted-foreground text-sm mb-6">
        Automatically remind clients about pending estimates they haven't responded to.
      </p>

      {/* Enable Toggle */}
      <div className="p-4 border rounded-xl mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Enable Auto Follow-Ups</p>
            <p className="text-sm text-muted-foreground mt-1">
              Send automatic email reminders for sent estimates that haven't been approved.
            </p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={v => setSettings(prev => ({...prev, enabled: v}))}
            data-testid="follow-up-enabled-toggle"
          />
        </div>
      </div>

      {settings.enabled && (
        <>
          {/* Reminder Schedule */}
          <div className="p-4 border rounded-xl mb-4">
            <p className="font-medium mb-3">Reminder Schedule</p>
            <p className="text-sm text-muted-foreground mb-3">Send reminders after this many days from when the estimate was sent:</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {settings.reminder_days.map(day => (
                <Badge key={day} variant="secondary" className="text-sm px-3 py-1 cursor-pointer hover:bg-red-100" onClick={() => removeDay(day)}>
                  Day {day} <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="number" min="1" max="90"
                value={newDay} onChange={e => setNewDay(e.target.value)}
                placeholder="Add day..."
                className="w-24 h-8 text-sm"
                onKeyDown={e => e.key === 'Enter' && addDay()}
                data-testid="add-reminder-day"
              />
              <Button size="sm" className="h-8" onClick={addDay}><Plus className="w-3 h-3" /></Button>
            </div>
          </div>

          {/* Max Reminders */}
          <div className="p-4 border rounded-xl mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Max Reminders Per Estimate</p>
                <p className="text-sm text-muted-foreground mt-1">Stop sending after this many reminders</p>
              </div>
              <Select value={String(settings.max_reminders)} onValueChange={v => setSettings(prev => ({...prev, max_reminders: parseInt(v)}))}>
                <SelectTrigger className="w-20 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Custom Message */}
          <div className="p-4 border rounded-xl mb-4">
            <p className="font-medium mb-2">Custom Reminder Message (Optional)</p>
            <p className="text-xs text-muted-foreground mb-2">
              Use {'{client_name}'}, {'{service_type}'}, {'{total}'} as placeholders. Leave blank for default messages.
            </p>
            <Textarea
              value={settings.message_template || ''}
              onChange={e => setSettings(prev => ({...prev, message_template: e.target.value || null}))}
              placeholder="Hi {client_name}, just a friendly reminder about your {service_type} estimate for {total}..."
              rows={3}
              className="text-sm"
              data-testid="follow-up-message-template"
            />
          </div>
        </>
      )}

      <Button onClick={handleSave} className="btn-primary" data-testid="save-follow-up-settings-btn">
        <Save className="w-4 h-4 mr-2" /> {t('save')} Follow-Up Settings
      </Button>
    </div>
  );
};

// Checklist Settings Section (used in Documents tab)
const AutomationSettingsSection = () => {
  const { organization, user, refreshSession } = useAuth();
  const [autoSchedule, setAutoSchedule] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const flags = organization?.feature_flags || {};
    // Default ON unless explicitly false
    setAutoSchedule(flags.auto_schedule_on_deposit_paid !== false);
  }, [organization?.feature_flags]);

  const handleToggle = async (checked) => {
    setAutoSchedule(checked);
    setIsSaving(true);
    try {
      const updatedFlags = {
        ...(organization.feature_flags || {}),
        auto_schedule_on_deposit_paid: checked,
      };
      await apiHelpers.updateOrganization(organization.id, { feature_flags: updatedFlags }, user?.id);
      if (refreshSession) await refreshSession();
      toast.success(checked ? 'Automation enabled' : 'Automation disabled');
    } catch (e) {
      toast.error('Failed to save setting');
      setAutoSchedule(!checked);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card-default" data-testid="automation-settings-section">
      <h2 className="font-heading text-xl font-semibold mb-2">Workflow Automation</h2>
      <p className="text-muted-foreground text-sm mb-6">
        Save time with smart automations that move work forward without manual steps.
      </p>

      <div className="p-4 border rounded-xl">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <p className="font-medium">Auto-create job when deposit is paid</p>
            <p className="text-sm text-muted-foreground mt-1">
              When a client pays their deposit invoice, automatically create a Job linked to the estimate
              and notify you to set the start date and assign your crew.
            </p>
          </div>
          <Switch
            checked={autoSchedule}
            onCheckedChange={handleToggle}
            disabled={isSaving}
            data-testid="auto-schedule-toggle"
          />
        </div>
      </div>
    </div>
  );
};


const ChecklistSettingsSection = () => {
  const { organization, user } = useAuth();
  const [requireChecklist, setRequireChecklist] = useState(true);
  const [defaultItems, setDefaultItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [newItem, setNewItem] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', items: [], is_default: false });
  const [templateNewItem, setTemplateNewItem] = useState('');

  useEffect(() => {
    if (organization?.id) loadSettings();
  }, [organization?.id]);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const [settingsRes, templatesRes] = await Promise.all([
        apiHelpers.getChecklistSettings(organization.id),
        apiHelpers.getChecklistTemplates(organization.id),
      ]);
      setRequireChecklist(settingsRes.data.require_before_conversion);
      setDefaultItems(settingsRes.data.default_items || []);
      setTemplates(templatesRes.data || []);
    } catch {
      // Use defaults
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await apiHelpers.updateChecklistSettings(organization.id, {
        require_before_conversion: requireChecklist,
        default_items: defaultItems,
      }, user.id);
      toast.success('Checklist settings saved');
    } catch {
      toast.error('Failed to save settings');
    }
  };

  const handleAddItem = () => {
    if (newItem.trim()) {
      setDefaultItems(prev => [...prev, newItem.trim()]);
      setNewItem('');
    }
  };

  const handleRemoveItem = (idx) => {
    setDefaultItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || templateForm.items.length === 0) {
      toast.error('Template needs a name and at least one item');
      return;
    }
    try {
      await apiHelpers.createChecklistTemplate(organization.id, templateForm, user.id);
      toast.success('Template created');
      setShowTemplateForm(false);
      setTemplateForm({ name: '', description: '', items: [], is_default: false });
      loadSettings();
    } catch {
      toast.error('Failed to create template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await apiHelpers.deleteChecklistTemplate(organization.id, templateId, user.id);
      toast.success('Template deleted');
      loadSettings();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  if (isLoading) return null;

  return (
    <div className="card-default" data-testid="checklist-settings-section">
      <h2 className="font-heading text-xl font-semibold mb-2">Checklist Settings</h2>
      <p className="text-muted-foreground text-sm mb-6">
        Configure checklist requirements for site visit conversions and manage checklist templates.
      </p>

      {/* Require Checklist Toggle */}
      <div className="p-4 border rounded-xl mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Require Checklist Before Conversion</p>
            <p className="text-sm text-muted-foreground mt-1">
              When enabled, field notes checklist must be completed before a site visit can be converted to an estimate.
            </p>
          </div>
          <Switch
            checked={requireChecklist}
            onCheckedChange={setRequireChecklist}
            data-testid="require-checklist-toggle"
          />
        </div>
      </div>

      {/* Default Checklist Items */}
      <div className="p-4 border rounded-xl mb-4">
        <p className="font-medium mb-3">Default Checklist Items</p>
        <div className="space-y-2 mb-3">
          {defaultItems.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded-lg">
              <span className="text-sm">{item}</span>
              <button
                onClick={() => handleRemoveItem(idx)}
                className="text-red-500 hover:text-red-700 p-1"
                data-testid={`remove-checklist-item-${idx}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newItem}
            onChange={e => setNewItem(e.target.value)}
            placeholder="Add checklist item..."
            className="text-sm h-9"
            onKeyDown={e => e.key === 'Enter' && handleAddItem()}
            data-testid="new-checklist-item-input"
          />
          <Button size="sm" onClick={handleAddItem} className="h-9" data-testid="add-checklist-item-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Button onClick={handleSaveSettings} className="btn-primary mb-6" data-testid="save-checklist-settings-btn">
        <Save className="w-4 h-4 mr-2" /> Save Checklist Settings
      </Button>

      {/* Checklist Templates */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-heading font-semibold">Checklist Templates</p>
            <p className="text-sm text-muted-foreground">Reusable checklist templates for different project types.</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowTemplateForm(true)} data-testid="add-checklist-template-btn">
            <Plus className="w-4 h-4 mr-1" /> New Template
          </Button>
        </div>

        {templates.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No custom templates yet.</p>
        ) : (
          <div className="space-y-3">
            {templates.map(tpl => (
              <div key={tpl.id} className="p-3 border rounded-xl" data-testid={`checklist-template-${tpl.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{tpl.name}</p>
                    {tpl.is_default && (
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">Default</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteTemplate(tpl.id)}
                    className="text-red-500 hover:text-red-700 p-1"
                    data-testid={`delete-template-${tpl.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {tpl.description && <p className="text-xs text-muted-foreground mb-2">{tpl.description}</p>}
                <div className="flex flex-wrap gap-1">
                  {(tpl.items || []).map((item, idx) => (
                    <span key={idx} className="text-xs px-2 py-1 bg-gray-100 rounded">{item}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Template Form */}
        {showTemplateForm && (
          <div className="mt-4 p-4 border-2 border-dashed rounded-xl" data-testid="checklist-template-form">
            <p className="font-medium text-sm mb-3">New Checklist Template</p>
            <div className="space-y-3">
              <Input
                value={templateForm.name}
                onChange={e => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Template name (e.g., Roofing Inspection)"
                className="text-sm h-9"
                data-testid="template-name-input"
              />
              <Input
                value={templateForm.description}
                onChange={e => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description (optional)"
                className="text-sm h-9"
              />
              <div className="space-y-1">
                {templateForm.items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <Check className="w-3 h-3 text-green-600" />
                    <span className="flex-1">{item}</span>
                    <button onClick={() => setTemplateForm(prev => ({
                      ...prev, items: prev.items.filter((_, i) => i !== idx)
                    }))} className="text-red-500 p-1">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={templateNewItem}
                  onChange={e => setTemplateNewItem(e.target.value)}
                  placeholder="Add item..."
                  className="text-sm h-8"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && templateNewItem.trim()) {
                      setTemplateForm(prev => ({ ...prev, items: [...prev.items, templateNewItem.trim()] }));
                      setTemplateNewItem('');
                    }
                  }}
                  data-testid="template-item-input"
                />
                <Button size="sm" className="h-8" onClick={() => {
                  if (templateNewItem.trim()) {
                    setTemplateForm(prev => ({ ...prev, items: [...prev.items, templateNewItem.trim()] }));
                    setTemplateNewItem('');
                  }
                }}>
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={templateForm.is_default}
                  onChange={e => setTemplateForm(prev => ({ ...prev, is_default: e.target.checked }))}
                />
                Set as default template for new site visits
              </label>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveTemplate} data-testid="save-template-btn">
                  <Save className="w-4 h-4 mr-1" /> Save Template
                </Button>
                <Button size="sm" variant="outline" onClick={() => {
                  setShowTemplateForm(false);
                  setTemplateForm({ name: '', description: '', items: [], is_default: false });
                }}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const { organization, user, updateOrganization, hasPermission } = useAuth();
  const developerMode = useDeveloperMode();
  const { currentLanguage, setLanguage, supportedLanguages, t } = useLanguage();
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  
  // Permission checks for different sections
  const isOwnerOrAdmin = user?.role === 'admin' || user?.role === 'owner';
  const isManager = user?.role === 'manager';
  const canAccessSettings = hasPermission('access_settings') || isOwnerOrAdmin;
  const canManageUsers = hasPermission('manage_users') || isOwnerOrAdmin;
  const canManageTeam = canManageUsers || isManager; // Managers can manage crew
  const canManageBilling = hasPermission('manage_billing') || isOwnerOrAdmin;
  const canManageTemplates = hasPermission('manage_templates') || isOwnerOrAdmin || isManager;
  const canExportData = hasPermission('export_data') || isOwnerOrAdmin;
  
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: 'staff',
    status: 'active'
  });
  
  // Employee state
  const [employees, setEmployees] = useState([]);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    role_type: 'crew_member',  // Must match CrewRoleType enum values
    notes: '',
    is_active: true
  });
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvPreview, setCsvPreview] = useState([]);
  
  // Email test state
  const [testEmail, setTestEmail] = useState('');
  const [emailTestStatus, setEmailTestStatus] = useState(null); // 'sending', 'success', 'error'
  const [emailTestMessage, setEmailTestMessage] = useState('');
  
  // Archived customers state
  const [archivedCustomers, setArchivedCustomers] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  
  // E-Signature audit trail state
  const [eSignatures, setESignatures] = useState([]);
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  
  // Terms state
  const [estimateTerms, setEstimateTerms] = useState(organization?.estimate_terms || '');
  const [invoiceTerms, setInvoiceTerms] = useState(organization?.invoice_terms || '');
  
  // Language settings
  const [orgDefaultLanguage, setOrgDefaultLanguage] = useState('en');
  const [userLanguagePreference, setUserLanguagePreference] = useState('org_default');
  
  // Company settings - expanded with new fields
  const [companyForm, setCompanyForm] = useState({
    company_name: '',
    phone: '',
    email: '',
    address: '',
    website: '',
    license_number: '',
    licensed_insured: true,
    brand_primary_color: '#1e3a8a',
    brand_accent_color: '#fbbf24',
    logo_url: '',
    service_types_enabled: [],
    business_type: 'general_contractor',
    default_deposit_percent: 50,
    default_tax_percent: 8.25,
    default_payment_terms: 'Payment due upon completion unless otherwise specified.',
    default_estimate_terms: DEFAULT_ESTIMATE_TERMS,
    enabled_estimate_modes: ['simple', 'detailed'],
    google_review_url: '',
    facebook_url: '',
    instagram_url: '',
    review_request_enabled: true,
  });
  
  // New user form
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'staff' });
  
  // Change password state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Payment settings state
  const [paymentSettings, setPaymentSettings] = useState({
    // Accepted payment methods
    accept_card_online: true,
    accept_cash: true,
    accept_check: true,
    accept_venmo: false,
    accept_cashapp: false,
    accept_paypal: false,
    
    // Primary Pay Now link
    primary_provider: 'stripe',
    primary_payment_url: '',
    
    // Alternate payment links
    venmo_handle: '',
    venmo_link: '',
    cashapp_cashtag: '',
    cashapp_link: '',
    paypal_link: '',
    
    // Check instructions
    check_payable_to: '',
    check_mailing_address: '',
    
    // Payment instructions
    payment_instructions: '',
    
    // Deposit rules
    require_deposit: true,
    deposit_type: 'percent',
    deposit_percent: 50,
    deposit_fixed_amount: 0
  });
  
  // Terms & Conditions settings
  const [termsSettings, setTermsSettings] = useState({
    use_template: true,
    custom_terms: '',
    require_acceptance: true
  });
  
  // Client Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    notify_on_status_change: false,
    notify_statuses: ['in_progress', 'completed'],
    review_url: '',
    review_incentive_text: '',
    show_review_incentive: false
  });

  useEffect(() => {
    if (organization) {
      setCompanyForm({
        company_name: organization.company_name || '',
        phone: organization.phone || '',
        email: organization.email || '',
        address: organization.address || '',
        website: organization.website || '',
        license_number: organization.license_number || '',
        licensed_insured: organization.licensed_insured !== false,
        brand_primary_color: organization.brand_primary_color || '#1e3a8a',
        brand_accent_color: organization.brand_accent_color || '#fbbf24',
        logo_url: organization.logo_url || '',
        service_types_enabled: organization.service_types_enabled || [],
        business_type: organization.business_type || 'general_contractor',
        default_deposit_percent: organization.default_deposit_percent || 50,
        default_tax_percent: organization.default_tax_percent || 8.25,
        default_payment_terms: organization.default_payment_terms || 'Payment due upon completion unless otherwise specified.',
        default_estimate_terms: organization.default_estimate_terms || DEFAULT_ESTIMATE_TERMS,
        enabled_estimate_modes: organization.enabled_estimate_modes || ['simple', 'detailed'],
        google_review_url: organization.google_review_url || '',
        facebook_url: organization.facebook_url || '',
        instagram_url: organization.instagram_url || '',
        review_request_enabled: organization.review_request_enabled !== false,
      });
      
      // Load payment settings
      if (organization.payment_settings) {
        setPaymentSettings(prev => ({
          ...prev,
          ...organization.payment_settings
        }));
      }
      
      // Load terms settings
      if (organization.terms_settings) {
        setTermsSettings(prev => ({
          ...prev,
          ...organization.terms_settings
        }));
      }
      
      // Load notification settings
      if (organization.notification_settings) {
        setNotificationSettings(prev => ({
          ...prev,
          ...organization.notification_settings
        }));
      }
      
      // Load language settings
      if (organization.preferences?.default_language) {
        setOrgDefaultLanguage(organization.preferences.default_language);
      }
      
      // Load user language preference
      const savedPref = localStorage.getItem('user_language_preference');
      if (savedPref) {
        setUserLanguagePreference(savedPref);
      }
      
      loadUsers();
      loadEmployees();
    }
  }, [organization]);

  const loadUsers = async () => {
    if (!organization?.id) return;
    try {
      const response = await apiHelpers.getUsers(organization.id);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setUserForm({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      role: userToEdit.role || 'staff',
      status: userToEdit.status || 'active'
    });
    setShowAddUser(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.name || !userForm.email) {
      toast.error('Name and email are required');
      return;
    }
    
    try {
      if (editingUser) {
        // Update existing user
        await apiHelpers.updateUser(organization.id, editingUser.id, {
          name: userForm.name,
          role: userForm.role,
          status: userForm.status
        }, user.id);
        toast.success('User updated!');
      } else {
        // Create new user (invite)
        await apiHelpers.inviteUser(organization.id, {
          email: userForm.email,
          name: userForm.name,
          role: userForm.role
        }, user.id);
        toast.success('User invited!');
      }
      setShowAddUser(false);
      setEditingUser(null);
      setUserForm({ name: '', email: '', role: 'staff', status: 'active' });
      loadUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      // Extract error message properly - backend may return object or string
      let errorMsg = 'Failed to save user';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors come as array
          errorMsg = error.response.data.detail.map(e => e.msg || e.message || String(e)).join(', ');
        } else if (typeof error.response.data.detail === 'object') {
          errorMsg = error.response.data.detail.msg || error.response.data.detail.message || JSON.stringify(error.response.data.detail);
        }
      }
      toast.error(errorMsg);
    }
  };

  const handleToggleUserStatus = async (userToToggle) => {
    if (userToToggle.id === user.id) {
      toast.error("You cannot change your own status");
      return;
    }
    
    const newStatus = userToToggle.status === 'active' ? 'inactive' : 'active';
    
    try {
      await apiHelpers.updateUser(organization.id, userToToggle.id, {
        status: newStatus
      }, user.id);
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      loadUsers();
    } catch (error) {
      console.error('Failed to update user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === user.id) {
      toast.error("You cannot remove yourself");
      return;
    }
    
    // First ask if they want to delete or deactivate
    const wantsPermanentDelete = await showConfirm({
      title: 'Remove User',
      description: 'Would you like to permanently delete this user and all their data, or just deactivate them?',
      variant: 'warning',
      confirmText: 'Permanently Delete',
      cancelText: 'Just Deactivate',
      destructive: true
    });
    
    try {
      if (wantsPermanentDelete) {
        // Confirm permanent delete
        const confirmDelete = await showConfirm({
          title: 'Confirm Permanent Delete',
          description: 'This will permanently remove this user and cannot be undone. Are you sure?',
          variant: 'danger',
          confirmText: 'Yes, Delete',
          cancelText: 'Cancel',
          destructive: true
        });
        
        if (confirmDelete) {
          await apiHelpers.deleteUser(organization.id, userId, user.id);
          toast.success('User permanently deleted');
          loadUsers();
        }
      } else {
        // Just deactivate
        await apiHelpers.deactivateUser(organization.id, userId, user.id);
        toast.success('User deactivated');
        loadUsers();
      }
    } catch (error) {
      console.error('Failed to remove user:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', role: 'staff', status: 'active' });
    setShowAddUser(true);
  };
  
  const loadEmployees = async () => {
    if (!organization?.id) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/employees`);
      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  const handleSaveEmployee = async () => {
    if (!employeeForm.first_name || !employeeForm.last_name) {
      toast.error('First and last name are required');
      return;
    }
    
    try {
      const url = editingEmployee 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/employees/${editingEmployee.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/employees`;
      
      const response = await fetch(url, {
        method: editingEmployee ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(employeeForm)
      });
      
      if (response.ok) {
        toast.success(editingEmployee ? 'Employee updated!' : 'Employee added!');
        setShowAddEmployee(false);
        setEditingEmployee(null);
        setEmployeeForm({ first_name: '', last_name: '', phone: '', email: '', role_type: 'crew_member', notes: '', is_active: true });
        loadEmployees();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save employee');
      }
    } catch (error) {
      console.error('Failed to save employee:', error);
      toast.error('Failed to save employee');
    }
  };
  
  const handleDeleteEmployee = async (employeeId) => {
    // First ask if they want to delete or deactivate
    const wantsPermanentDelete = await showConfirm({
      title: 'Remove Employee',
      description: 'Would you like to permanently delete this employee, or just deactivate them?',
      variant: 'warning',
      confirmText: 'Permanently Delete',
      cancelText: 'Just Deactivate',
      destructive: true
    });
    
    try {
      if (wantsPermanentDelete) {
        // Confirm permanent delete
        const confirmDelete = await showConfirm({
          title: 'Confirm Permanent Delete',
          description: 'This will permanently remove this employee and cannot be undone. Are you sure?',
          variant: 'danger',
          confirmText: 'Yes, Delete',
          cancelText: 'Cancel',
          destructive: true
        });
        
        if (confirmDelete) {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/employees/${employeeId}?permanent=true`,
            { method: 'DELETE' }
          );
          
          if (response.ok) {
            toast.success('Employee permanently deleted');
            loadEmployees();
          } else {
            throw new Error('Delete failed');
          }
        }
      } else {
        // Just deactivate
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/employees/${employeeId}`,
          { 
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ is_active: false })
          }
        );
        
        if (response.ok) {
          toast.success('Employee deactivated');
          loadEmployees();
        } else {
          throw new Error('Deactivate failed');
        }
      }
    } catch (error) {
      console.error('Failed to delete/deactivate employee:', error);
      toast.error('Failed to update employee');
    }
  };

  const handleToggleEmployeeStatus = async (employee) => {
    const newStatus = employee.is_active !== false;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/employees/${employee.id}`,
        { 
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_active: !newStatus })
        }
      );
      
      if (response.ok) {
        toast.success(`Employee ${newStatus ? 'deactivated' : 'activated'}`);
        loadEmployees();
      }
    } catch (error) {
      console.error('Failed to toggle employee status:', error);
      toast.error('Failed to update employee status');
    }
  };
  
  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEmployeeForm({
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      phone: employee.phone || '',
      email: employee.email || '',
      role_type: employee.role_type || 'crew_member',
      notes: employee.notes || '',
      is_active: employee.is_active !== false
    });
    setShowAddEmployee(true);
  };
  
  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setEmployeeForm({ first_name: '', last_name: '', phone: '', email: '', role_type: 'crew_member', notes: '', is_active: true });
    setShowAddEmployee(true);
  };
  
  // Archived customers functions
  const loadArchivedCustomers = async () => {
    if (!organization?.id) return;
    setLoadingArchived(true);
    try {
      const response = await apiHelpers.getArchivedClients(organization.id);
      setArchivedCustomers(response.data || []);
    } catch (error) {
      console.error('Failed to load archived customers:', error);
    } finally {
      setLoadingArchived(false);
    }
  };

  const handleRestoreCustomer = async (client) => {
    try {
      await apiHelpers.restoreClient(organization.id, client.id, user.id);
      toast.success(`"${client.name}" restored to your active customers`);
      loadArchivedCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore customer');
    }
  };

  const handlePermanentDeleteCustomer = async (client) => {
    const confirmed = await showConfirm({
      title: `Delete "${client.name}"?`,
      description: 'This will permanently remove all their records. This action cannot be undone!',
      variant: 'danger',
      confirmText: 'Delete Forever',
      cancelText: 'Cancel',
      destructive: true
    });
    
    if (!confirmed) return;
    
    try {
      await apiHelpers.permanentlyDeleteClient(organization.id, client.id, user.id);
      toast.success(`"${client.name}" permanently deleted`);
      loadArchivedCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const loadESignatures = async () => {
    if (!organization?.id) return;
    setLoadingSignatures(true);
    try {
      const response = await api.get(`/organizations/${organization.id}/esignatures`);
      setESignatures(response.data.signatures || []);
    } catch (error) {
      console.error('Failed to load e-signatures:', error);
    } finally {
      setLoadingSignatures(false);
    }
  };
  
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      const data = lines.slice(1).map(line => {
        const values = line.split(',');
        const row = {};
        headers.forEach((header, i) => {
          row[header] = values[i]?.trim() || '';
        });
        return row;
      });
      
      setCsvData(data);
      setCsvPreview(data.slice(0, 5));
      setShowCsvImport(true);
    };
    reader.readAsText(file);
  };
  
  const handleCsvImport = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/employees/import`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(csvData)
        }
      );
      
      const result = await response.json();
      toast.success(`Imported ${result.imported} crew members (${result.skipped} skipped)`);
      setShowCsvImport(false);
      setCsvData([]);
      setCsvPreview([]);
      loadEmployees();
    } catch (error) {
      console.error('Failed to import:', error);
      toast.error('Failed to import crew');
    }
  };
  
  const downloadCsvTemplate = () => {
    const csv = 'first_name,last_name,phone,email,role_type,notes\nJohn,Smith,555-1234,john@example.com,crew,Example note\nJane,Doe,555-5678,jane@example.com,estimator,';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveCompany = async () => {
    setIsLoading(true);
    try {
      // Strip locked fields — niches are admin-managed only
      // eslint-disable-next-line no-unused-vars
      const { business_type, secondary_niches, ...editable } = companyForm;
      await apiHelpers.updateOrganization(organization.id, editable, user.id);
      updateOrganization(editable);
      toast.success('Settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleChangePassword = async () => {
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsChangingPassword(true);
    try {
      await api.post(`/auth/change-password?user_id=${user.id}`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };
  
  const handleSaveOrgLanguage = async () => {
    setIsLoading(true);
    try {
      const updatedPreferences = {
        ...organization.preferences,
        default_language: orgDefaultLanguage
      };
      await apiHelpers.updateOrganization(organization.id, { preferences: updatedPreferences }, user.id);
      updateOrganization({ ...organization, preferences: updatedPreferences });
      toast.success('Organization language saved!');
    } catch (error) {
      console.error('Failed to save language:', error);
      toast.error('Failed to save language settings');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSaveUserLanguage = async () => {
    setIsLoading(true);
    try {
      const langToSave = userLanguagePreference === 'org_default' ? null : userLanguagePreference;
      await apiHelpers.updateUserLanguage(organization.id, user.id, langToSave);
      
      // Update local state and context
      localStorage.setItem('user_language_preference', userLanguagePreference);
      if (userLanguagePreference === 'org_default') {
        setLanguage(orgDefaultLanguage);
      } else {
        setLanguage(userLanguagePreference);
      }
      
      toast.success('Your language preference saved!');
    } catch (error) {
      console.error('Failed to save user language:', error);
      toast.error('Failed to save language preference');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSavePaymentSettings = async () => {
    setIsLoading(true);
    try {
      await apiHelpers.updateOrganization(organization.id, { payment_settings: paymentSettings }, user.id);
      updateOrganization({ ...organization, payment_settings: paymentSettings });
      toast.success('Payment settings saved!');
    } catch (error) {
      console.error('Failed to save payment settings:', error);
      toast.error('Failed to save payment settings');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSaveTermsSettings = async () => {
    setIsLoading(true);
    try {
      await apiHelpers.updateOrganization(organization.id, { terms_settings: termsSettings }, user.id);
      updateOrganization({ ...organization, terms_settings: termsSettings });
      toast.success('Terms & Conditions saved!');
    } catch (error) {
      console.error('Failed to save terms settings:', error);
      toast.error('Failed to save terms settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNotificationSettings = async () => {
    setIsLoading(true);
    try {
      await apiHelpers.updateOrganization(organization.id, { notification_settings: notificationSettings }, user.id);
      updateOrganization({ ...organization, notification_settings: notificationSettings });
      toast.success('Notification settings saved!');
    } catch (error) {
      console.error('Failed to save notification settings:', error);
      toast.error('Failed to save notification settings');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleNotifyStatus = (status) => {
    setNotificationSettings(prev => ({
      ...prev,
      notify_statuses: prev.notify_statuses.includes(status)
        ? prev.notify_statuses.filter(s => s !== status)
        : [...prev.notify_statuses, status]
    }));
  };

  const toggleService = (service) => {
    setCompanyForm(prev => ({
      ...prev,
      service_types_enabled: prev.service_types_enabled.includes(service)
        ? prev.service_types_enabled.filter(s => s !== service)
        : [...prev.service_types_enabled, service]
    }));
  };

  // Old handleAddUser - renamed to avoid conflict, kept for backwards compatibility
  const handleInviteNewUser = async () => {
    if (!newUser.name || !newUser.email) {
      toast.error('Please fill in name and email');
      return;
    }
    
    try {
      const response = await apiHelpers.inviteUser(organization.id, {
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        send_invite_email: true
      }, user.id);
      
      if (response.data.email_sent) {
        toast.success('Invitation sent!', { description: `An email has been sent to ${newUser.email}` });
      } else {
        toast.success('User added!', { description: `${newUser.email} was added but email could not be sent` });
      }
      
      setShowAddUser(false);
      setNewUser({ name: '', email: '', role: 'staff' });
      loadUsers();
    } catch (error) {
      console.error('Failed to invite user:', error);
      toast.error('Failed to invite user', { 
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  const handleResendInvite = async (userId, email) => {
    try {
      const response = await apiHelpers.resendInvite(organization.id, userId);
      if (response.data.email_sent) {
        toast.success('Invitation resent!', { description: `A new invitation was sent to ${email}` });
      } else {
        toast.warning('User updated', { description: 'Invitation generated but email could not be sent' });
      }
      loadUsers();
    } catch (error) {
      console.error('Failed to resend invite:', error);
      toast.error('Failed to resend invitation');
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      await apiHelpers.reactivateUser(organization.id, userId, user.id);
      toast.success('User reactivated');
      loadUsers();
    } catch (error) {
      console.error('Failed to reactivate user:', error);
      toast.error('Failed to reactivate user');
    }
  };

  const handleDeactivateUser = async (userId) => {
    const confirmed = await showConfirm({
      title: 'Deactivate User?',
      description: 'This user will no longer be able to access the system. You can reactivate them later.',
      variant: 'warning',
      confirmText: 'Deactivate',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      await apiHelpers.deactivateUser(organization.id, userId, user.id);
      toast.success('User deactivated');
      loadUsers();
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      toast.error('Failed to deactivate user');
    }
  };

  const handleExport = async (type) => {
    try {
      let response;
      let filename;
      
      switch (type) {
        case 'clients':
          response = await apiHelpers.exportClients(organization.id);
          filename = 'clients.csv';
          break;
        case 'estimates':
          response = await apiHelpers.exportEstimates(organization.id);
          filename = 'estimates.csv';
          break;
        case 'invoices':
          response = await apiHelpers.exportInvoices(organization.id);
          filename = 'invoices.csv';
          break;
        default:
          return;
      }
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${type} exported!`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    }
  };

  const handleRemoveLogo = async () => {
    const confirmed = await showConfirm({
      title: 'Remove Logo?',
      description: 'This will remove your company logo from estimates, invoices, and other documents.',
      variant: 'warning',
      confirmText: 'Remove',
      cancelText: 'Keep It'
    });
    
    if (!confirmed) return;
    
    toast.loading('Removing logo...', { id: 'logo-remove' });
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/logo?user_id=${user.id}`,
        { method: 'DELETE' }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setCompanyForm(prev => ({ ...prev, logo_url: '' }));
        updateOrganization({ ...organization, logo_url: '' });
        toast.success('Logo removed', { id: 'logo-remove' });
      } else {
        toast.error('Failed to remove logo', { id: 'logo-remove' });
      }
    } catch (error) {
      console.error('Logo remove error:', error);
      toast.error('Failed to remove logo', { id: 'logo-remove' });
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setEmailTestStatus('sending');
    setEmailTestMessage('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/test/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_email: testEmail, org_id: organization?.id })
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setEmailTestStatus('success');
        setEmailTestMessage('Test email sent! Check your inbox.');
        toast.success('Test email sent successfully!');
      } else {
        setEmailTestStatus('error');
        setEmailTestMessage(data.message || 'Failed to send test email');
        toast.error(data.message || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Email test failed:', error);
      setEmailTestStatus('error');
      setEmailTestMessage('Failed to send test email. Please check your configuration.');
      toast.error('Failed to send test email');
    }
  };

  const isOwner = user?.role === 'owner';

  // Access denied for users without settings permission
  if (!canAccessSettings) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4 animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
          <Lock className="w-8 h-8 text-red-500" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-foreground">Access Denied</h1>
        <p className="text-muted-foreground text-center max-w-md">
          You don't have permission to access Settings. Please contact your administrator if you need access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
          {t('settings_title')}
        </h1>
        <p className="text-muted-foreground text-lg mt-1">
          {t('manage_settings')}
        </p>
      </div>
      
      <Tabs defaultValue={new URLSearchParams(window.location.search).get('tab') || 'business'} className="space-y-6" orientation="vertical" onValueChange={(value) => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (value === 'data') loadArchivedCustomers();
      }}>
        {/* Desktop: Vertical sidebar tabs, Mobile: Horizontal scroll */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Tabs Navigation - Sticky and compact */}
          <TabsList className="flex lg:flex-col w-full lg:w-48 h-auto lg:sticky lg:top-4 lg:self-start overflow-x-auto lg:overflow-visible gap-1 p-1.5 bg-slate-100/80 rounded-xl">
            <TabsTrigger value="business" className="flex-shrink-0 h-10 px-3 text-sm font-medium justify-start gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">{t('business')}</span>
            </TabsTrigger>
            {canManageTeam && (
              <TabsTrigger value="team" className="flex-shrink-0 h-10 px-3 text-sm font-medium justify-start gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="team-tab">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">{t('team')}</span>
              </TabsTrigger>
            )}
            {canManageTemplates && (
              <TabsTrigger value="documents" className="flex-shrink-0 h-10 px-3 text-sm font-medium justify-start gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="documents-tab">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">{t('documents')}</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="notifications" className="flex-shrink-0 h-10 px-3 text-sm font-medium justify-start gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="notifications-tab">
              <Bell className="w-4 h-4" />
              <span className="hidden sm:inline">{t('notifications')}</span>
            </TabsTrigger>
            {canManageBilling && (
              <TabsTrigger value="payments" className="flex-shrink-0 h-10 px-3 text-sm font-medium justify-start gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="payments-tab">
                <CreditCard className="w-4 h-4" />
                <span className="hidden sm:inline">Payments</span>
              </TabsTrigger>
            )}
            {canManageTemplates && (
              <TabsTrigger value="signatures" className="flex-shrink-0 h-10 px-3 text-sm font-medium justify-start gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="signatures-tab" onClick={loadESignatures}>
                <FileSignature className="w-4 h-4" />
                <span className="hidden sm:inline">{t('esignatures')}</span>
              </TabsTrigger>
            )}
            {canExportData && (
              <TabsTrigger value="data" className="flex-shrink-0 h-10 px-3 text-sm font-medium justify-start gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="data-tab">
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">{t('data')}</span>
              </TabsTrigger>
            )}
          </TabsList>
          
          {/* Developer Mode link (below tabs, not a tab itself) — only visible
              when the platform admin has toggled Developer Mode on for this
              org. Default hidden. */}
          {developerMode && (
            <div className="lg:w-48 flex-shrink-0">
              <Link
                to="/sync-queue"
                className="flex items-center gap-2 px-3 py-2 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="settings-sync-queue-link"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Sync queue (dev)</span>
              </Link>
            </div>
          )}
          
          {/* Tab Content Area */}
          <div className="flex-1 min-w-0">
        
        {/* BUSINESS TAB - Company, Branding, Language combined */}
        <TabsContent value="business">
          <div className="space-y-8 max-w-3xl">
            {/* Company Information Section */}
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-4">{t('company_information')}</h2>
            
            <div className="space-y-5">
              <div>
                <Label className="text-base font-medium">{t('company_name')}</Label>
                <Input
                  value={companyForm.company_name}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, company_name: e.target.value }))}
                  className="input-default mt-2"
                  data-testid="company-name-input"
                />
              </div>

              {/* Business Niche Tags — READ-ONLY for contractors, admin-managed */}
              <div data-testid="business-niche-readonly">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-base font-medium">Business Niche &amp; Services</Label>
                  <span className="text-[10px] uppercase tracking-wide font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                    Admin-managed
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-gray-50 border border-gray-200 min-h-[48px]">
                  {(() => {
                    const primary = organization?.business_type || organization?.industry;
                    const secondaries = organization?.secondary_niches || [];
                    const labelOf = (raw) => {
                      const map = {
                        construction: 'General Construction',
                        general_contractor: 'General Contractor',
                        concrete: 'Concrete',
                        roofing: 'Roofing',
                        fencing: 'Fencing',
                        decking: 'Decking',
                        painting: 'Painting',
                        plumbing: 'Plumbing',
                        electrical: 'Electrical',
                        hvac: 'HVAC',
                        lawn_care: 'Lawn Care',
                        landscaping: 'Landscaping',
                        detailing: 'Vehicle Detailing',
                        cleaning: 'Cleaning',
                      };
                      const key = String(raw || '').toLowerCase().replace(/\s+/g, '_');
                      return map[key] || (raw ? String(raw).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : null);
                    };
                    if (!primary && secondaries.length === 0) {
                      return <span className="text-sm text-muted-foreground italic">No niche set yet — contact your account manager to set this up.</span>;
                    }
                    return (
                      <>
                        {primary && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 text-blue-800 text-sm font-semibold border border-blue-200" data-testid="niche-tag-primary">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                            {labelOf(primary)}
                            <span className="text-[9px] uppercase tracking-wide opacity-70">Primary</span>
                          </span>
                        )}
                        {secondaries.map((n) => (
                          <span key={n} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-sm font-medium border border-gray-200" data-testid={`niche-tag-${n}`}>
                            {labelOf(n)}
                          </span>
                        ))}
                      </>
                    );
                  })()}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Your niche tags determine the measurement options, photo categories, and checklists you see throughout ContractorOS. <strong>Need to update?</strong> Contact your account manager.
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-base font-medium">Phone</Label>
                  <Input
                    value={companyForm.phone}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="input-default mt-2"
                  />
                </div>
                <div>
                  <Label className="text-base font-medium">{t('email')}</Label>
                  <Input
                    type="email"
                    value={companyForm.email}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, email: e.target.value }))}
                    className="input-default mt-2"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-base font-medium">{t('address')}</Label>
                <Input
                  value={companyForm.address}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, address: e.target.value }))}
                  className="input-default mt-2"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-base font-medium">{t('website_label')}</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Globe className="w-5 h-5 text-muted-foreground" />
                    <Input
                      value={companyForm.website}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="www.yourcompany.com"
                      className="input-default"
                    />
                  </div>
                </div>
                <div>
                  <Label>{t('license_optional')}</Label>
                  <Input
                    value={companyForm.license_number}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, license_number: e.target.value }))}
                    placeholder="e.g., LIC-123456"
                    className="input-default mt-1"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <Label className="text-base">{t('licensed_insured')}</Label>
                  <p className="text-sm text-muted-foreground">{t('licensed_insured_desc')}</p>
                </div>
                <Switch
                  checked={companyForm.licensed_insured}
                  onCheckedChange={(checked) => setCompanyForm(prev => ({ ...prev, licensed_insured: checked }))}
                />
              </div>

              {/* Reviews & social — drives the public completion review page */}
              <div className="p-4 rounded-xl border border-gray-200 bg-white space-y-3" data-testid="reviews-social-section">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-base">Reviews &amp; Social</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Used on the customer-facing completion page after a job is marked complete.</p>
                  </div>
                  <Switch
                    checked={companyForm.review_request_enabled}
                    onCheckedChange={(checked) => setCompanyForm(prev => ({ ...prev, review_request_enabled: checked }))}
                    data-testid="review-request-toggle"
                  />
                </div>
                <div>
                  <Label className="text-sm">Google review link</Label>
                  <Input
                    value={companyForm.google_review_url}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, google_review_url: e.target.value }))}
                    placeholder="https://g.page/r/.../review"
                    className="input-default mt-1.5 text-sm"
                    data-testid="google-review-url-input"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    From your Google Business Profile → Get more reviews → Share. Customers who give 4–5 stars are nudged to share publicly.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Facebook page URL</Label>
                    <Input
                      value={companyForm.facebook_url}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, facebook_url: e.target.value }))}
                      placeholder="https://facebook.com/yourpage"
                      className="input-default mt-1.5 text-sm"
                      data-testid="facebook-url-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Instagram URL</Label>
                    <Input
                      value={companyForm.instagram_url}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, instagram_url: e.target.value }))}
                      placeholder="https://instagram.com/yourhandle"
                      className="input-default mt-1.5 text-sm"
                      data-testid="instagram-url-input"
                    />
                  </div>
                </div>
              </div>
              
              <Button
                onClick={handleSaveCompany}
                disabled={isLoading}
                className="btn-primary mt-6"
                data-testid="save-company-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-5 h-5" />
                    Save Changes
                  </span>
                )}
              </Button>
              
              {/* Change Password Section */}
              <div className="mt-10 pt-8 border-t border-gray-200">
                <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-primary" />
                  Change Your Password
                </h3>
                <div className="max-w-md space-y-4">
                  <div>
                    <Label>{t('current_password')}</Label>
                    <Input
                      type="password"
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                      placeholder="Enter your current password"
                      className="mt-1.5 h-11"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <Label>{t('new_password')}</Label>
                    <Input
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                      placeholder="Enter new password (min 6 characters)"
                      className="mt-1.5 h-11"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <Label>Confirm New Password</Label>
                    <Input
                      type="password"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                      placeholder="Confirm new password"
                      className="mt-1.5 h-11"
                      data-testid="confirm-new-password-input"
                    />
                  </div>
                  <Button
                    onClick={handleChangePassword}
                    disabled={isChangingPassword || !passwordForm.current_password || !passwordForm.new_password}
                    variant="outline"
                    className="mt-2"
                    data-testid="change-password-btn"
                  >
                    {isChangingPassword ? 'Changing...' : 'Change Password'}
                  </Button>
                </div>
              </div>
            </div>
            </div>
            
            {/* Branding Section */}
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-4">{t('brand_colors')}</h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => setCompanyForm(prev => ({
                      ...prev,
                      brand_primary_color: preset.primary,
                      brand_accent_color: preset.accent
                    }))}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      companyForm.brand_primary_color === preset.primary 
                        ? 'border-primary ring-2 ring-primary/20' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: preset.primary }} />
                      <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: preset.accent }} />
                    </div>
                    <p className="text-sm font-medium">{preset.name}</p>
                  </button>
                ))}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('primary_color')}</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={companyForm.brand_primary_color}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, brand_primary_color: e.target.value }))}
                      className="w-14 h-14 rounded-xl cursor-pointer border-2 border-gray-200"
                    />
                    <Input
                      value={companyForm.brand_primary_color}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, brand_primary_color: e.target.value }))}
                      className="input-default flex-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>{t('accent_color')}</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={companyForm.brand_accent_color}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, brand_accent_color: e.target.value }))}
                      className="w-14 h-14 rounded-xl cursor-pointer border-2 border-gray-200"
                    />
                    <Input
                      value={companyForm.brand_accent_color}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, brand_accent_color: e.target.value }))}
                      className="input-default flex-1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Logo Upload Section */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Company Logo</Label>
                <p className="text-sm text-muted-foreground">Upload your company logo. Recommended size: 300x100 pixels (PNG or JPG).</p>
                
                <div className="flex items-start gap-4">
                  {/* Logo Preview */}
                  <div className="w-32 h-24 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-gray-50 overflow-hidden">
                    {companyForm.logo_url ? (
                      <img 
                        src={companyForm.logo_url} 
                        alt="Logo preview" 
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<span class="text-xs text-muted-foreground">Failed to load</span>';
                        }}
                      />
                    ) : (
                      <div className="text-center">
                        <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-1" />
                        <span className="text-xs text-muted-foreground">No logo</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Upload Controls */}
                  <div className="flex-1 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            // Validate file size (max 2MB)
                            if (file.size > 2 * 1024 * 1024) {
                              toast.error('File too large. Maximum size is 2MB.');
                              return;
                            }
                            
                            // Read file as base64
                            const reader = new FileReader();
                            reader.onload = async () => {
                              const base64 = reader.result;
                              
                              // Upload to backend
                              toast.loading('Uploading logo...', { id: 'logo-upload' });
                              try {
                                const response = await fetch(
                                  `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/upload-logo`,
                                  {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      logo_data: base64,
                                      filename: file.name,
                                      mime_type: file.type || 'image/png'
                                    })
                                  }
                                );
                                
                                const data = await response.json();
                                
                                if (data.success) {
                                  setCompanyForm(prev => ({ ...prev, logo_url: data.logo_url }));
                                  updateOrganization({ ...organization, logo_url: data.logo_url });
                                  toast.success('Logo uploaded!', { id: 'logo-upload' });
                                } else {
                                  toast.error(data.message || 'Failed to upload logo', { id: 'logo-upload' });
                                }
                              } catch (error) {
                                console.error('Logo upload error:', error);
                                toast.error('Failed to upload logo', { id: 'logo-upload' });
                              }
                            };
                            reader.readAsDataURL(file);
                          }}
                          data-testid="logo-file-input"
                        />
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                          <Upload className="w-4 h-4" />
                          Upload Logo
                        </span>
                      </label>
                      
                      {companyForm.logo_url && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveLogo}
                          className="text-red-600 hover:text-red-700"
                          data-testid="remove-logo-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                    
                    {/* Optional: Manual URL input */}
                    <div className="text-xs text-muted-foreground">
                      Or enter a URL directly:
                    </div>
                    <Input
                      value={companyForm.logo_url || ''}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, logo_url: e.target.value }))}
                      placeholder="https://example.com/logo.png"
                      className="input-default text-sm h-9"
                    />
                  </div>
                </div>
              </div>
              
              {/* Preview */}
              <div className="p-6 rounded-2xl border border-gray-200">
                <p className="text-sm text-muted-foreground mb-3">Preview:</p>
                <div className="flex items-center gap-4">
                  {companyForm.logo_url ? (
                    <img 
                      src={companyForm.logo_url} 
                      alt="Company logo" 
                      className="h-12 max-w-[120px] object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl"
                      style={{ backgroundColor: companyForm.brand_primary_color }}
                    >
                      {companyForm.company_name?.charAt(0) || 'C'}
                    </div>
                  )}
                  <div>
                    <p className="font-heading font-semibold text-lg">
                      {companyForm.company_name || 'Your Company'}
                    </p>
                    <button 
                      className="px-4 py-1 rounded-lg text-sm font-bold mt-1"
                      style={{ backgroundColor: companyForm.brand_accent_color }}
                    >
                      New Estimate
                    </button>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={handleSaveCompany}
                disabled={isLoading}
                className="btn-primary"
                data-testid="save-branding-btn"
              >
                <Save className="w-5 h-5 mr-2" />
                Save Changes
              </Button>
            </div>
            </div>
            
            {/* Language Section */}
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-2">Language</h2>
              <p className="text-muted-foreground text-sm mb-4">
                Set the default language for your organization.
              </p>
              
              {isOwner && (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium">Organization Language</p>
                      <p className="text-xs text-muted-foreground">Default for all team members</p>
                    </div>
                  </div>
                  <Select value={orgDefaultLanguage} onValueChange={(val) => {
                    setOrgDefaultLanguage(val);
                    handleSaveOrgDefaultLanguage(val);
                  }}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {supportedLanguages.map(lang => (
                        <SelectItem key={lang.code} value={lang.code}>
                          <span className="flex items-center gap-2">
                            <span>{lang.flag}</span>
                            <span>{lang.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
        
        {/* TEAM TAB - Users + Crew combined */}
        <TabsContent value="team">
          <div className="space-y-8 max-w-4xl">
            {/* Users Section - Only visible to those who can manage users */}
            {canManageUsers && (
            <div className="card-default">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                <div>
                  <h2 className="font-heading text-xl font-semibold">{t('users')}</h2>
                  <p className="text-muted-foreground text-sm">Team members with login access</p>
                </div>
                <Button onClick={handleAddUser} className="btn-primary w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>
              
              <div className="space-y-3">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-4 border rounded-xl hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        u.status === 'inactive' ? 'bg-gray-100 text-gray-500' :
                        u.role === 'owner' ? 'bg-amber-100 text-amber-700' : 
                        u.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {formatRoleName(u.role)}
                      </span>
                      {u.status === 'inactive' && (
                        <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-600">
                          Inactive
                        </span>
                      )}
                      {/* Only show edit/delete for non-owner users or if current user is owner */}
                      {u.id !== user.id && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditUser(u)}
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleUserStatus(u)}
                            className={`h-8 w-8 ${u.status === 'inactive' ? 'text-green-600 hover:text-green-700' : 'text-muted-foreground hover:text-amber-600'}`}
                            title={u.status === 'inactive' ? 'Activate user' : 'Deactivate user'}
                            data-testid={`toggle-user-${u.id}`}
                          >
                            {u.status === 'inactive' ? <CheckCircle className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                          </Button>
                          {u.role !== 'owner' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteUser(u.id)}
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      )}
                      {u.id === user.id && (
                        <span className="text-xs text-muted-foreground italic">(You)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}
            
            {/* Crew Section - Visible to those who can manage team */}
            <div className="card-default">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                <div>
                  <h2 className="font-heading text-xl font-semibold">Crew Members</h2>
                  <p className="text-muted-foreground text-sm">Field workers without login access</p>
                </div>
                <Button onClick={handleAddEmployee} className="btn-primary w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Crew
                </Button>
              </div>
              
              {employees.length > 0 ? (
                <div className="space-y-3">
                  {employees.map((emp) => (
                    <div key={emp.id} className="flex items-center justify-between p-4 border rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                          <UserCog className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {emp.first_name && emp.last_name 
                              ? `${emp.first_name} ${emp.last_name}` 
                              : emp.name || 'Unnamed'}
                          </p>
                          <p className="text-xs text-muted-foreground">{formatRoleName(emp.role_type) || formatRoleName(emp.role) || 'Crew Member'}</p>
                          {emp.phone && <p className="text-xs text-muted-foreground">{emp.phone}</p>}
                          {emp.email && <p className="text-xs text-muted-foreground">{emp.email}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          emp.is_active !== false && emp.status !== 'inactive' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {emp.is_active !== false && emp.status !== 'inactive' ? 'Active' : 'Inactive'}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditEmployee(emp)}
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          data-testid={`edit-employee-${emp.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteEmployee(emp.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          data-testid={`delete-employee-${emp.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <UserCog className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No crew members yet</p>
                  <p className="text-sm">Add your field workers here</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
        
        {/* DOCUMENTS TAB - Estimates, Terms, Defaults combined */}
        <TabsContent value="documents">
          <div className="space-y-8 max-w-3xl">
            {/* Estimate Settings Section */}
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-2">{t('estimate_settings')}</h2>
              <p className="text-muted-foreground text-sm mb-6">Configure how estimates work for your business workflow.</p>
              
              {/* Estimate Display Modes */}
              <div className="p-4 border rounded-xl mb-4">
                <div className="flex-1 mb-4">
                  <p className="font-medium">Estimate Display Modes</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Choose which estimate formats are available when creating estimates. This helps reduce clutter by only showing the modes you use.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[
                    { id: 'simple', label: 'Simple', desc: 'Scope of work + work items' },
                    { id: 'detailed', label: 'Itemized', desc: 'Detailed line items with qty/rate' },
                    { id: 'summary', label: 'Summary', desc: 'Category descriptions with totals' },
                    { id: 'sections', label: 'Combined', desc: 'Multiple sections, each approvable' }
                  ].map((mode) => {
                    const currentModes = companyForm.enabled_estimate_modes || organization?.enabled_estimate_modes || ['simple', 'detailed'];
                    const isEnabled = currentModes.includes(mode.id);
                    
                    return (
                      <label
                        key={mode.id}
                        className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          isEnabled ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={(e) => {
                            let newModes = [...currentModes];
                            if (e.target.checked) {
                              if (!newModes.includes(mode.id)) {
                                newModes.push(mode.id);
                              }
                            } else {
                              // Ensure at least one mode is enabled
                              if (newModes.length > 1) {
                                newModes = newModes.filter(m => m !== mode.id);
                              } else {
                                toast.error('At least one mode must be enabled');
                                return;
                              }
                            }
                            setCompanyForm(prev => ({ ...prev, enabled_estimate_modes: newModes }));
                          }}
                          className="mt-1"
                        />
                        <div>
                          <p className="font-medium text-sm">{mode.label}</p>
                          <p className="text-xs text-muted-foreground">{mode.desc}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <Button
                  onClick={async () => {
                    try {
                      const newModes = companyForm.enabled_estimate_modes || organization?.enabled_estimate_modes || ['simple', 'detailed'];
                      await apiHelpers.updateOrganization(organization.id, { enabled_estimate_modes: newModes }, user.id);
                      updateOrganization({ ...organization, enabled_estimate_modes: newModes });
                      toast.success('Estimate modes saved!');
                    } catch (error) {
                      console.error('Error saving estimate modes:', error);
                      toast.error('Failed to save estimate modes');
                    }
                  }}
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Estimate Modes
                </Button>
              </div>
              
              {/* Line Items Mode */}
              <div className="p-4 border rounded-xl mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-medium">{t('line_items_during_visit')}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Choose how line items appear when creating a new estimate.
                    </p>
                  </div>
                  <Select
                    value={organization?.preferences?.line_items_mode || 'optional'}
                    onValueChange={async (value) => {
                      try {
                        const updatedPreferences = {
                          ...organization.preferences,
                          line_items_mode: value,
                          require_line_items_on_estimate: value === 'required'
                        };
                        await apiHelpers.updateOrganization(organization.id, { preferences: updatedPreferences }, user.id);
                        updateOrganization({ ...organization, preferences: updatedPreferences });
                        toast.success('Setting saved');
                      } catch (error) {
                        toast.error('Failed to update');
                      }
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="required">{t('required')}</SelectItem>
                      <SelectItem value="optional">{t('optional')}</SelectItem>
                      <SelectItem value="hidden">{t('hidden')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Checklist Settings Section */}
            <ChecklistSettingsSection />
            
            {/* Follow-Up Settings Section */}
            <FollowUpSettingsSection />
            
            {/* Workflow Automation */}
            <AutomationSettingsSection />
            
            {/* Terms & Conditions Section */}
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-2">{t('terms_conditions')}</h2>
              <p className="text-muted-foreground text-sm mb-4">Default text shown on estimates and invoices.</p>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Estimate Terms</Label>
                  <Textarea
                    value={estimateTerms}
                    onChange={(e) => setEstimateTerms(e.target.value)}
                    className="h-24 mt-1.5"
                    placeholder="Enter terms that appear on estimates..."
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Invoice Terms</Label>
                  <Textarea
                    value={invoiceTerms}
                    onChange={(e) => setInvoiceTerms(e.target.value)}
                    className="h-24 mt-1.5"
                    placeholder="Enter terms that appear on invoices..."
                  />
                </div>
                <Button onClick={handleSaveCompany} disabled={isLoading} className="btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  Save Terms
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* NOTIFICATIONS TAB - Email & Alerts combined */}
        <TabsContent value="notifications">
          <div className="space-y-8 max-w-3xl">
            {/* SMS Notifications Section */}
            <div className="card-default">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold">SMS Notifications</h2>
                  <p className="text-muted-foreground text-sm">Send text message updates to your customers</p>
                </div>
              </div>
              
              <div className="mt-6 space-y-4">
                {/* SMS Phone Number Configuration - Read Only for contractors */}
                <div className="p-4 border rounded-xl bg-gray-50">
                  <div className="flex items-start gap-3">
                    <Smartphone className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <Label className="font-medium">Your SMS Phone Number</Label>
                      <p className="text-sm text-muted-foreground mb-2">This number is used to send SMS notifications to your customers</p>
                      {organization?.sms_settings?.twilio_phone_number ? (
                        <div className="flex items-center gap-2 p-3 bg-white border rounded-lg max-w-xs">
                          <Phone className="w-4 h-4 text-green-600" />
                          <span className="font-medium">{organization.sms_settings.twilio_phone_number}</span>
                          <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg max-w-xs">
                          <AlertCircle className="w-4 h-4 text-amber-600" />
                          <span className="text-amber-700">Not assigned yet</span>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        Contact your administrator to get a phone number assigned
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* SMS Toggle Options */}
                <div className="flex items-center justify-between p-4 border rounded-xl">
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">Enable SMS Notifications</p>
                      <p className="text-sm text-muted-foreground">Send SMS for appointments & job updates</p>
                    </div>
                  </div>
                  <Switch
                    checked={organization?.sms_settings?.enabled === true}
                    disabled={!organization?.sms_settings?.twilio_phone_number}
                    onCheckedChange={async (checked) => {
                      if (!organization?.sms_settings?.twilio_phone_number) {
                        toast.error('Please add a Twilio phone number first');
                        return;
                      }
                      try {
                        const smsSettings = { ...organization.sms_settings, enabled: checked };
                        await apiHelpers.updateOrganization(organization.id, { sms_settings: smsSettings }, user.id);
                        updateOrganization({ ...organization, sms_settings: smsSettings });
                        toast.success(checked ? 'SMS enabled' : 'SMS disabled');
                      } catch (error) {
                        toast.error('Failed to update');
                      }
                    }}
                    data-testid="sms-enabled-toggle"
                  />
                </div>
                
                {organization?.sms_settings?.enabled && (
                  <>
                    <div className="flex items-center justify-between p-4 border rounded-xl">
                      <div>
                        <p className="font-medium">Site Visit Reminders</p>
                        <p className="text-sm text-muted-foreground">Text customers when a site visit is scheduled</p>
                      </div>
                      <Switch
                        checked={organization?.sms_settings?.notify_site_visit !== false}
                        onCheckedChange={async (checked) => {
                          try {
                            const smsSettings = { ...organization.sms_settings, notify_site_visit: checked };
                            await apiHelpers.updateOrganization(organization.id, { sms_settings: smsSettings }, user.id);
                            updateOrganization({ ...organization, sms_settings: smsSettings });
                            toast.success('Setting saved');
                          } catch (error) {
                            toast.error('Failed to update');
                          }
                        }}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 border rounded-xl">
                      <div>
                        <p className="font-medium">Job Status Updates</p>
                        <p className="text-sm text-muted-foreground">Text when jobs start, complete, or change status</p>
                      </div>
                      <Switch
                        checked={organization?.sms_settings?.notify_job_status !== false}
                        onCheckedChange={async (checked) => {
                          try {
                            const smsSettings = { ...organization.sms_settings, notify_job_status: checked };
                            await apiHelpers.updateOrganization(organization.id, { sms_settings: smsSettings }, user.id);
                            updateOrganization({ ...organization, sms_settings: smsSettings });
                            toast.success('Setting saved');
                          } catch (error) {
                            toast.error('Failed to update');
                          }
                        }}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 border rounded-xl">
                      <div>
                        <p className="font-medium">Estimate Notifications</p>
                        <p className="text-sm text-muted-foreground">Text customers when an estimate is sent</p>
                      </div>
                      <Switch
                        checked={organization?.sms_settings?.notify_estimate === true}
                        onCheckedChange={async (checked) => {
                          try {
                            const smsSettings = { ...organization.sms_settings, notify_estimate: checked };
                            await apiHelpers.updateOrganization(organization.id, { sms_settings: smsSettings }, user.id);
                            updateOrganization({ ...organization, sms_settings: smsSettings });
                            toast.success('Setting saved');
                          } catch (error) {
                            toast.error('Failed to update');
                          }
                        }}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 border rounded-xl">
                      <div>
                        <p className="font-medium">Invoice Notifications</p>
                        <p className="text-sm text-muted-foreground">Text customers when an invoice is sent</p>
                      </div>
                      <Switch
                        checked={organization?.sms_settings?.notify_invoice === true}
                        onCheckedChange={async (checked) => {
                          try {
                            const smsSettings = { ...organization.sms_settings, notify_invoice: checked };
                            await apiHelpers.updateOrganization(organization.id, { sms_settings: smsSettings }, user.id);
                            updateOrganization({ ...organization, sms_settings: smsSettings });
                            toast.success('Setting saved');
                          } catch (error) {
                            toast.error('Failed to update');
                          }
                        }}
                      />
                    </div>
                  </>
                )}
                
                {!organization?.sms_settings?.twilio_phone_number && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex gap-3">
                      <MessageSquare className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-800">Want SMS notifications?</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Contact your BirdenMedia administrator to get a phone number assigned to your account.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Email Notifications Section */}
            <div className="card-default">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold">Email Notifications</h2>
                  <p className="text-muted-foreground text-sm">Configure email settings for your organization</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-xl">
                  <div>
                    <p className="font-medium">Send Estimate Notifications</p>
                    <p className="text-sm text-muted-foreground">Email customers when estimates are created</p>
                  </div>
                  <Switch
                    checked={organization?.preferences?.send_estimate_emails !== false}
                    onCheckedChange={async (checked) => {
                      try {
                        const updatedPreferences = { ...organization.preferences, send_estimate_emails: checked };
                        await apiHelpers.updateOrganization(organization.id, { preferences: updatedPreferences }, user.id);
                        updateOrganization({ ...organization, preferences: updatedPreferences });
                        toast.success('Setting saved');
                      } catch (error) {
                        toast.error('Failed to update');
                      }
                    }}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-xl">
                  <div>
                    <p className="font-medium">Send Invoice Notifications</p>
                    <p className="text-sm text-muted-foreground">Email customers when invoices are created</p>
                  </div>
                  <Switch
                    checked={organization?.preferences?.send_invoice_emails !== false}
                    onCheckedChange={async (checked) => {
                      try {
                        const updatedPreferences = { ...organization.preferences, send_invoice_emails: checked };
                        await apiHelpers.updateOrganization(organization.id, { preferences: updatedPreferences }, user.id);
                        updateOrganization({ ...organization, preferences: updatedPreferences });
                        toast.success('Setting saved');
                      } catch (error) {
                        toast.error('Failed to update');
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* PAYMENTS TAB */}
        <TabsContent value="payments">
          <div className="space-y-6 max-w-3xl">
            {/* Stripe Connect onboarding */}
            <StripeConnectCard orgId={organization?.id} />

            {/* Accepted Payment Methods */}
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-2">Accepted Payment Methods</h2>
              <p className="text-muted-foreground text-sm mb-6">Toggle which payment methods your customers can use and add your payment links</p>
              
              <div className="space-y-4">
                {/* Card / Stripe — toggle only; Stripe Connect above is the source of truth */}
                <div className="p-4 border rounded-xl hover:bg-gray-50 transition-colors" data-testid="card-payment-toggle">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <div>
                        <span className="font-medium">Card payments</span>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {paymentSettings.accept_card_online
                            ? 'Customers see the Pay Now button (requires Stripe connected above).'
                            : 'Hides the Pay Now button on invoices, even if Stripe is connected.'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={paymentSettings.accept_card_online}
                      onCheckedChange={(checked) => setPaymentSettings(prev => ({ ...prev, accept_card_online: checked }))}
                      data-testid="card-online-switch"
                    />
                  </div>
                </div>
                
                {/* Cash */}
                <div className="p-4 border rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Banknote className="w-5 h-5 text-green-600" />
                      <span className="font-medium">Cash</span>
                    </div>
                    <Switch
                      checked={paymentSettings.accept_cash}
                      onCheckedChange={(checked) => setPaymentSettings(prev => ({ ...prev, accept_cash: checked }))}
                    />
                  </div>
                </div>
                
                {/* Check */}
                <div className="p-4 border rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-600" />
                      <span className="font-medium">Check</span>
                    </div>
                    <Switch
                      checked={paymentSettings.accept_check}
                      onCheckedChange={(checked) => setPaymentSettings(prev => ({ ...prev, accept_check: checked }))}
                    />
                  </div>
                  {paymentSettings.accept_check && (
                    <div className="mt-3 pl-8 space-y-3">
                      <div>
                        <Label className="text-sm text-muted-foreground">Make check payable to</Label>
                        <Input
                          placeholder="Your Business Name"
                          value={paymentSettings.check_payable_to || ''}
                          onChange={(e) => setPaymentSettings(prev => ({ ...prev, check_payable_to: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Mailing address</Label>
                        <Input
                          placeholder="123 Main St, City, State ZIP"
                          value={paymentSettings.check_mailing_address || ''}
                          onChange={(e) => setPaymentSettings(prev => ({ ...prev, check_mailing_address: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Venmo */}
                <div className="p-4 border rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-[#008CFF] rounded text-white text-xs flex items-center justify-center font-bold">V</span>
                      <span className="font-medium">Venmo</span>
                    </div>
                    <Switch
                      checked={paymentSettings.accept_venmo}
                      onCheckedChange={(checked) => setPaymentSettings(prev => ({ ...prev, accept_venmo: checked }))}
                    />
                  </div>
                  {paymentSettings.accept_venmo && (
                    <div className="mt-3 pl-8 space-y-3">
                      <div>
                        <Label className="text-sm text-muted-foreground">Venmo @handle</Label>
                        <Input
                          placeholder="@YourBusiness"
                          value={paymentSettings.venmo_handle || ''}
                          onChange={(e) => setPaymentSettings(prev => ({ ...prev, venmo_handle: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Venmo Payment Link (optional)</Label>
                        <Input
                          placeholder="https://venmo.com/u/YourBusiness"
                          value={paymentSettings.venmo_link || ''}
                          onChange={(e) => setPaymentSettings(prev => ({ ...prev, venmo_link: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Cash App */}
                <div className="p-4 border rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-[#00D632] rounded text-white text-xs flex items-center justify-center font-bold">$</span>
                      <span className="font-medium">Cash App</span>
                    </div>
                    <Switch
                      checked={paymentSettings.accept_cashapp}
                      onCheckedChange={(checked) => setPaymentSettings(prev => ({ ...prev, accept_cashapp: checked }))}
                    />
                  </div>
                  {paymentSettings.accept_cashapp && (
                    <div className="mt-3 pl-8 space-y-3">
                      <div>
                        <Label className="text-sm text-muted-foreground">Cash App $cashtag</Label>
                        <Input
                          placeholder="$YourBusiness"
                          value={paymentSettings.cashapp_cashtag || ''}
                          onChange={(e) => setPaymentSettings(prev => ({ ...prev, cashapp_cashtag: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Cash App Payment Link (optional)</Label>
                        <Input
                          placeholder="https://cash.app/$YourBusiness"
                          value={paymentSettings.cashapp_link || ''}
                          onChange={(e) => setPaymentSettings(prev => ({ ...prev, cashapp_link: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Zelle */}
                <div className="p-4 border rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-[#6D1ED4] rounded text-white text-xs flex items-center justify-center font-bold">Z</span>
                      <span className="font-medium">Zelle</span>
                    </div>
                    <Switch
                      checked={paymentSettings.accept_zelle || false}
                      onCheckedChange={(checked) => setPaymentSettings(prev => ({ ...prev, accept_zelle: checked }))}
                    />
                  </div>
                  {paymentSettings.accept_zelle && (
                    <div className="mt-3 pl-8">
                      <Label className="text-sm text-muted-foreground">Zelle Email or Phone</Label>
                      <Input
                        placeholder="payments@yourbusiness.com or (555) 123-4567"
                        value={paymentSettings.zelle_id || ''}
                        onChange={(e) => setPaymentSettings(prev => ({ ...prev, zelle_id: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Payment Instructions */}
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-2">Payment Instructions</h2>
              <p className="text-muted-foreground text-sm mb-4">Add any special instructions that will appear on invoices</p>
              <Textarea
                placeholder="e.g., Payment is due within 30 days of invoice date. Late payments may incur a 2% monthly fee."
                value={paymentSettings.payment_instructions || ''}
                onChange={(e) => setPaymentSettings(prev => ({ ...prev, payment_instructions: e.target.value }))}
                rows={3}
              />
            </div>
            
            <Button onClick={handleSavePaymentSettings} disabled={isLoading} className="btn-primary">
              <Save className="w-4 h-4 mr-2" />
              Save Payment Settings
            </Button>
          </div>
        </TabsContent>

        {/* E-SIGNATURES TAB - Audit Trail */}
        <TabsContent value="signatures">
          <div className="space-y-6 max-w-4xl">
            <div className="card-default">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileSignature className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold">E-Signature Audit Trail</h2>
                  <p className="text-muted-foreground text-sm">View all electronically signed estimates</p>
                </div>
              </div>

              {loadingSignatures ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : eSignatures.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileSignature className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>{t('no_esignatures')}</p>
                  <p className="text-sm mt-1">When clients sign estimates, they'll appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {eSignatures.map((sig) => (
                    <div key={sig.id} className="border rounded-xl p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold">{sig.client_name || 'Client'}</span>
                            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                              Signed
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {sig.title || `Estimate #${sig.id.slice(-8)}`} • ${sig.total?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div className="text-sm text-right">
                          <p className="font-medium">{sig.e_signature?.signer_name}</p>
                          <p className="text-muted-foreground text-xs">
                            {new Date(sig.e_signature?.signed_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      {/* Signature Details (Expandable) */}
                      <details className="mt-3 pt-3 border-t">
                        <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 font-medium">
                          View Signature Details
                        </summary>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-muted-foreground">Signer:</span>
                            <span className="font-medium">{sig.e_signature?.signer_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="text-muted-foreground">Email:</span>
                            <span className="font-medium">{sig.e_signature?.signer_email || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span className="text-muted-foreground">Signed:</span>
                            <span className="font-medium">{new Date(sig.e_signature?.signed_at).toLocaleString()}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-gray-400" />
                            <span className="text-muted-foreground">IP:</span>
                            <span className="font-mono text-xs">{sig.e_signature?.ip_address}</span>
                          </div>
                          <div className="flex items-center gap-2 sm:col-span-2">
                            <Hash className="w-4 h-4 text-gray-400" />
                            <span className="text-muted-foreground">Document Hash:</span>
                            <span className="font-mono text-xs truncate">{sig.e_signature?.document_hash}</span>
                          </div>
                          <div className="flex items-center gap-2 sm:col-span-2">
                            <Shield className="w-4 h-4 text-green-500" />
                            <span className="text-green-700 font-medium">
                              {sig.e_signature?.terms_accepted ? 'Terms & Conditions Accepted' : 'Terms not recorded'}
                            </span>
                          </div>
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
        
        {/* DATA TAB - Export & Archived combined */}
        <TabsContent value="data">
          <div className="space-y-8 max-w-3xl">
            {/* Export Section */}
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-2">Export Data</h2>
              <p className="text-muted-foreground text-sm mb-4">Download your data as CSV files</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Button variant="outline" onClick={() => window.open(`${import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization?.id}/export/clients`, '_blank')}>
                  <Download className="w-4 h-4 mr-2" />
                  Customers
                </Button>
                <Button variant="outline" onClick={() => window.open(`${import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization?.id}/export/estimates`, '_blank')}>
                  <Download className="w-4 h-4 mr-2" />
                  Estimates
                </Button>
                <Button variant="outline" onClick={() => window.open(`${import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization?.id}/export/invoices`, '_blank')}>
                  <Download className="w-4 h-4 mr-2" />
                  Invoices
                </Button>
              </div>
            </div>
            
            {/* Archived Customers Section */}
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-2">Archived Customers</h2>
              <p className="text-muted-foreground text-sm mb-4">
                Archived customers are hidden from your active list but their records are preserved.
              </p>
              
              {loadingArchived ? (
                <div className="py-8 text-center text-muted-foreground">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-sm">Loading...</p>
                </div>
              ) : archivedCustomers.length > 0 ? (
                <div className="space-y-3">
                  {archivedCustomers.map(client => (
                    <div key={client.id} className="border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-muted/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          <User className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <h3 className="font-medium">{client.name}</h3>
                          <p className="text-xs text-muted-foreground">{client.email || client.phone || 'No contact'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleRestoreCustomer(client)} className="h-8 text-xs border-green-500 text-green-600">
                          <RotateCcw className="w-3.5 h-3.5 mr-1" />
                          Restore
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handlePermanentDeleteCustomer(client)} className="h-8 text-xs border-red-500 text-red-600">
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground border border-dashed rounded-lg">
                  <Archive className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="font-medium">No archived customers</p>
                  <p className="text-xs">When you archive a customer, they'll appear here</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
          </div>
        </div>
      </Tabs>
      
      {/* Add/Edit User Dialog */}
      <Dialog open={showAddUser} onOpenChange={(open) => {
        setShowAddUser(open);
        if (!open) {
          setEditingUser(null);
          setUserForm({ name: '', email: '', role: 'staff', status: 'active' });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingUser ? 'Edit Team Member' : 'Add Team Member'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Name</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                className="input-default mt-1"
                data-testid="user-form-name"
              />
            </div>
            
            <div>
              <Label>{t('email')}</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                className="input-default mt-1"
                disabled={!!editingUser}
                data-testid="user-form-email"
              />
              {editingUser && (
                <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
              )}
            </div>
            
            <div>
              <Label>{t('role')}</Label>
              <Select 
                value={userForm.role} 
                onValueChange={(value) => setUserForm(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger className="input-default mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="field_estimator">Field Estimator</SelectItem>
                  <SelectItem value="project_manager">Project Manager</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editingUser && (
              <div>
                <Label>Status</Label>
                <Select 
                  value={userForm.status} 
                  onValueChange={(value) => setUserForm(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="input-default mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddUser(false);
                  setEditingUser(null);
                  setUserForm({ name: '', email: '', role: 'staff', status: 'active' });
                }}
                className="flex-1 h-12"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveUser}
                className="flex-1 h-12 btn-primary"
                data-testid="confirm-save-user-btn"
              >
                {editingUser ? (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Save Changes
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5 mr-2" />
                    Add User
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Employee Dialog */}
      <Dialog open={showAddEmployee} onOpenChange={setShowAddEmployee}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl sm:text-2xl">
              {editingEmployee ? 'Edit Crew Member' : 'Add Crew Member'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={employeeForm.first_name}
                  onChange={(e) => setEmployeeForm(prev => ({ ...prev, first_name: e.target.value }))}
                  className="input-default mt-1"
                  placeholder="John"
                  data-testid="employee-first-name"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={employeeForm.last_name}
                  onChange={(e) => setEmployeeForm(prev => ({ ...prev, last_name: e.target.value }))}
                  className="input-default mt-1"
                  placeholder="Smith"
                  data-testid="employee-last-name"
                />
              </div>
            </div>
            
            <div>
              <Label>Phone</Label>
              <Input
                value={employeeForm.phone}
                onChange={(e) => setEmployeeForm(prev => ({ ...prev, phone: e.target.value }))}
                className="input-default mt-1"
                placeholder="555-123-4567"
                data-testid="employee-phone"
              />
            </div>
            
            <div>
              <Label>{t('email')}</Label>
              <Input
                type="email"
                value={employeeForm.email}
                onChange={(e) => setEmployeeForm(prev => ({ ...prev, email: e.target.value }))}
                className="input-default mt-1"
                placeholder="john@example.com"
                data-testid="employee-email"
              />
            </div>
            
            <div>
              <Label>{t('role')}</Label>
              <Select value={employeeForm.role_type} onValueChange={(value) => setEmployeeForm(prev => ({ ...prev, role_type: value }))}>
                <SelectTrigger className="input-default mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="crew_member">Crew Member</SelectItem>
                  <SelectItem value="crew_lead">Crew Lead</SelectItem>
                  <SelectItem value="field_estimator">Field Estimator</SelectItem>
                  <SelectItem value="project_manager">Project Manager</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {editingEmployee && (
              <div>
                <Label>Status</Label>
                <Select 
                  value={employeeForm.is_active ? 'active' : 'inactive'} 
                  onValueChange={(value) => setEmployeeForm(prev => ({ ...prev, is_active: value === 'active' }))}
                >
                  <SelectTrigger className="input-default mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Notes</Label>
              <Input
                value={employeeForm.notes}
                onChange={(e) => setEmployeeForm(prev => ({ ...prev, notes: e.target.value }))}
                className="input-default mt-1"
                placeholder="Any additional notes..."
                data-testid="employee-notes"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddEmployee(false);
                  setEditingEmployee(null);
                  setEmployeeForm({ first_name: '', last_name: '', phone: '', email: '', role_type: 'crew_member', notes: '' });
                }}
                className="flex-1 h-11 sm:h-12"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEmployee}
                className="flex-1 h-11 sm:h-12 btn-primary"
                data-testid="save-employee-btn"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {editingEmployee ? 'Update' : 'Add Crew'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Confirm Dialog */}
      {ConfirmDialogComponent}
    </div>
  );
};

export default SettingsPage;
